import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import dynamic from 'next/dynamic'
import { Analytics } from '@vercel/analytics/next'
import {
  Text,
  Group,
  Button,
  ColorScheme,
  MantineProvider,
  ColorSchemeProvider,
} from '@mantine/core'
import { AppProps } from 'next/app'
import { useRouter, NextRouter } from 'next/router'
import Head from 'next/head'
import Image from 'next/image'
import { NotificationsProvider } from '@mantine/notifications'
import { useMediaQuery } from '@mantine/hooks'
import { CookiesProvider, useCookies } from 'react-cookie'
import { init, show, configure } from 'cookie-though'
import {
  isIOS,
  isMacOs,
  isMobile,
  isSafari,
  isIPad13,
  isIPhone13,
  isMobileOnly,
  isMobileSafari,
} from 'react-device-detect'
import '../public/styles/styles.css'
import useStyles from './_app.styles'
import useFullscreen from '../app/components/hooks/useFullscreenMode'
import {
  HOST,
  IS_QA,
  LOGO_PNG,
  LOGO_AVIF,
  DEBUG_MODE,
  FAV_ICON_16,
  FAV_ICON_32,
  FAV_ICON_96,
  FAV_ICON_194,
  COOKIE_CONFIG,
  IS_PRODUCTION,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  PRODUCTION_HOST,
  APPLE_TOUCH_76_ICON,
  APPLE_TOUCH_72_ICON,
  APPLE_TOUCH_60_ICON,
  APPLE_TOUCH_57_ICON,
  APPLE_TOUCH_180_ICON,
  APPLE_TOUCH_152_ICON,
  APPLE_TOUCH_144_ICON,
  APPLE_TOUCH_120_ICON,
  APPLE_TOUCH_114_ICON,
  GOOGLE_ANALYTICS_TAG,
  USE_GOOGLE_ANALYTICS,
  SVG_ICON_CDN_OR_LOCAL,
  FAV_ICON_CDN_OR_LOCAL,
  BLOG_DESCRIPTION_HOME,
  ANDROID_CHROME_72_ICON,
  ANDROID_CHROME_48_ICON,
  ANDROID_CHROME_36_ICON,
  GOOGLE_ANALYTICS_SCRIPT,
  ANDROID_CHROME_512_ICON,
  ANDROID_CHROME_384_ICON,
  ANDROID_CHROME_256_ICON,
  ANDROID_CHROME_192_ICON,
  ANDROID_CHROME_144_ICON,
  ANDROID_CHROME_128_ICON,
  ERROR_PAGE_DISPLAY_NAME,
  BLOG_DESCRIPTION_HOME_ES,
  BLOG_DESCRIPTION_HOME_FR,
  BLOG_DESCRIPTION_HOME_KO,
  MS_TILE_144_CDN_OR_LOCAL,
  USE_ENV_PROTECTION_MODAL,
  MAIN_SITE_PRODUCTION_HOST,
  MAIN_SITE_ENVIRONMENT_HOST,
  ENV_PROTECTION_COOKIE_NAME,
  APPLE_TOUCH_STARTUP_IMAGE_1,
  APPLE_TOUCH_STARTUP_IMAGE_2,
  APPLE_TOUCH_STARTUP_IMAGE_3,
  APPLE_TOUCH_STARTUP_IMAGE_4,
  APPLE_TOUCH_STARTUP_IMAGE_5,
  APPLE_TOUCH_STARTUP_IMAGE_6,
  APPLE_TOUCH_STARTUP_IMAGE_7,
  APPLE_TOUCH_STARTUP_IMAGE_8,
  APPLE_TOUCH_STARTUP_IMAGE_9,
  APPLE_TOUCH_STARTUP_IMAGE_10,
  APPLE_TOUCH_STARTUP_IMAGE_11,
  APPLE_TOUCH_STARTUP_IMAGE_12,
  APPLE_TOUCH_STARTUP_IMAGE_13,
  APPLE_TOUCH_STARTUP_IMAGE_14,
  APPLE_TOUCH_STARTUP_IMAGE_15,
  APPLE_TOUCH_STARTUP_IMAGE_16,
  APPLE_TOUCH_STARTUP_IMAGE_17,
  APPLE_TOUCH_STARTUP_IMAGE_18,
  APPLE_TOUCH_STARTUP_IMAGE_19,
  APPLE_TOUCH_STARTUP_IMAGE_20,
  APPLE_TOUCH_STARTUP_IMAGE_21,
  APPLE_TOUCH_STARTUP_IMAGE_22,
  APPLE_TOUCH_STARTUP_IMAGE_23,
  APPLE_TOUCH_STARTUP_IMAGE_24,
  APPLE_TOUCH_STARTUP_IMAGE_25,
  APPLE_TOUCH_STARTUP_IMAGE_26,
  APPLE_TOUCH_STARTUP_IMAGE_27,
  APPLE_TOUCH_STARTUP_IMAGE_28,
  APPLE_TOUCH_STARTUP_IMAGE_29,
  APPLE_TOUCH_STARTUP_IMAGE_30,
  APPLE_TOUCH_STARTUP_IMAGE_31,
  APPLE_TOUCH_STARTUP_IMAGE_32,
  APPLE_TOUCH_STARTUP_IMAGE_33,
  APPLE_TOUCH_STARTUP_IMAGE_34,
  MAIN_SITE_ENVIRONMENT_HOST_ES,
  MAIN_SITE_ENVIRONMENT_HOST_FR,
  MAIN_SITE_ENVIRONMENT_HOST_KO,
  FOUR_O_FOUR_PAGE_DISPLAY_NAME,
  SELECTED_LANGUAGE_COOKIE_NAME,
  STREAMY3_OFFICIAL_ADDRESS_CITY,
  FIVE_HUNDRED_PAGE_DISPLAY_NAME,
  SAFARI_PINNED_TAB_CDN_OR_LOCAL,
  STREAMY3_OFFICIAL_BUSINESS_NAME,
  STREAMY3_INFORMAL_BUSINESS_NAME,
  STREAMY3_OFFICIAL_ADDRESS_LINE_1,
  STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE,
  STREAMY3_OFFICIAL_ADDRESS_STATE_CODE,
  PRACTICAL_STREAM_LOGO_LARGE_DARK_PNG,
  JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES,
  JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES_PIXELS_WIDTH,
  JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES_PIXELS_HEIGHT,
  HOME_PAGE_DISPLAY_NAME,
} from '../app/utilities/config'
import {
  isFrench,
  isKorean,
  translate,
  isSpanish,
  isEnglish,
  isValidLanguage,
  getNavigatorLanguage,
  LANGUAGE_CODE_ENGLISH,
  UNITED_STATES_ISO_3166_ALPHA_2_CODE,
} from '../app/utilities/language'
import { isNotNullOrEmptyString, toTrimmedString } from '../app/utilities/misc'
import {
  MANTINE_DARK_COLORS_THEME,
  HOME_SPLASH_GET_STARTED_BUTTON_SHADOW,
  HOME_SPLASH_GET_STARTED_BUTTON_BORDER_RADIUS,
} from '../app/utilities/styles'
import {
  HOW_MUCH_ARTICLE_URL,
  HOW_MUCH_ARTICLE_TITLE,
  HOW_MUCH_ARTICLE_URL_ES,
  HOW_MUCH_ARTICLE_URL_FR,
  HOW_MUCH_ARTICLE_URL_KO,
  HOW_MUCH_ARTICLE_TITLE_ES,
  HOW_MUCH_ARTICLE_TITLE_FR,
  HOW_MUCH_ARTICLE_TITLE_KO,
  HOW_MUCH_JSON_LD_IMAGE_1200_X_1200,
} from '../app/static-data/how-much-is-a-music-license-for-a-small-shop-data'
import {
  HOW_MUSIC_BOOSTS_ARTICLE_URL,
  HOW_MUSIC_BOOSTS_ARTICLE_TITLE,
  HOW_MUSIC_BOOSTS_ARTICLE_URL_ES,
  HOW_MUSIC_BOOSTS_ARTICLE_URL_FR,
  HOW_MUSIC_BOOSTS_ARTICLE_URL_KO,
  HOW_MUSIC_BOOSTS_ARTICLE_TITLE_ES,
  HOW_MUSIC_BOOSTS_ARTICLE_TITLE_FR,
  HOW_MUSIC_BOOSTS_ARTICLE_TITLE_KO,
  HOW_MUSIC_BOOSTS_JSON_LD_IMAGE_1200_X_1200,
} from '../app/static-data/how-music-boosts-productivity-data'
import {
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL,
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE,
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_ES,
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_FR,
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_KO,
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE_ES,
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE_FR,
  HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE_KO,
  HOW_THE_RIGHT_JSON_LD_IMAGE_1200_X_1200,
} from '../app/static-data/how-the-right-playlist-can-increase-restaurant-sales-data'
import {
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_ES,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_FR,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_KO,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE_ES,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE_FR,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE_KO,
  SECRET_SOUNDTRACK_FORMULA_JSON_LD_IMAGE_1200_X_1200,
} from '../app/static-data/secret-soundtrack-formula-for-gyms-and-cafes-data'
import {
  SPOTIFY_ISNT_LEGAL_ARTICLE_URL,
  SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE,
  SPOTIFY_ISNT_LEGAL_ARTICLE_URL_ES,
  SPOTIFY_ISNT_LEGAL_ARTICLE_URL_FR,
  SPOTIFY_ISNT_LEGAL_ARTICLE_URL_KO,
  SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE_ES,
  SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE_FR,
  SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE_KO,
  SPOTIFY_ISNT_LEGAL_JSON_LD_IMAGE_1200_X_1200,
} from '../app/static-data/spotify-isnt-legal-for-your-business-data'
import {
  HOW_TO_BUILD_ARTICLE_URL,
  HOW_TO_BUILD_ARTICLE_TITLE,
  HOW_TO_BUILD_ARTICLE_URL_ES,
  HOW_TO_BUILD_ARTICLE_URL_FR,
  HOW_TO_BUILD_ARTICLE_URL_KO,
  HOW_TO_BUILD_ARTICLE_TITLE_ES,
  HOW_TO_BUILD_ARTICLE_TITLE_FR,
  HOW_TO_BUILD_ARTICLE_TITLE_KO,
  HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200,
} from '../app/static-data/how-to-build-your-perfect-workplace-with-the-best-music-strategy-data'

const MaskModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/MaskModal'),
  {
    ssr: true,
  }
)
const EnvironmentProtectionModal: React.ComponentType<any> = dynamic(
  () => import('../app/components/controls/EnvironmentProtectionModal'),
  {
    ssr: true,
  }
)

const APPLE_STORE_LISTED: boolean = false
const USE_VERCEL_ANALYTICS: boolean = IS_PRODUCTION

export default function App(props: AppProps) {
  const {
    Component,
    pageProps: { session, ...pageProps },
  } = props
  const router: NextRouter = useRouter()
  const pageComponentDisplayName: any = Component?.displayName
  const isBot: boolean = pageProps?.ib == true ? true : false
  const rateLimited: boolean = pageProps?.rl == true ? true : false
  const [colorScheme, setColorScheme] = useState<ColorScheme>('dark')
  const { classes } = useStyles()
  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark'))
  const [cookies, setCookie] = useCookies<any>(['blog.practical.stream'])
  const isApple: boolean =
    isIOS || isMacOs || isIPhone13 || isIPad13 || isMobileSafari || isSafari
  const [inIframe, setInIframe] = useState<boolean>(false)
  if (typeof window !== 'undefined')
    useEffect(() => {
      try {
        setInIframe(window.self !== window.top)
      } catch (e) {
        if (DEBUG_MODE) console.log(e)
      }
    }, [window])
  const [canRenderComponent, setCanRenderComponent] = useState<boolean>(false)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      timeoutId = setTimeout(() => setCanRenderComponent(true), 50)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
    return () => {
      try {
        if (timeoutId != null) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      } catch {}
    }
  }, [])
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    isNotNullOrEmptyString(pageProps?.initialLang) &&
      (isEnglish(pageProps?.initialLang) ||
        isFrench(pageProps?.initialLang) ||
        isKorean(pageProps?.initialLang) ||
        isSpanish(pageProps?.initialLang))
      ? toTrimmedString(pageProps?.initialLang)
      : LANGUAGE_CODE_ENGLISH
  )
  const googleAnalyticsTag: JSX.Element = useMemo(() => {
    return (
      <script
        dangerouslySetInnerHTML={{
          __html: GOOGLE_ANALYTICS_SCRIPT,
        }}
        async
      />
    )
  }, [])
  const [isMacOsSafariLimitedRender, setIsMacOsSafariLimitedRender] =
    useState<boolean>(false)
  const [isMacOsSafariLessThanVersion15, setIsMacOsSafariLessThanVersion15] =
    useState<boolean>(false)
  const [
    environmentProtectionModalOpened,
    setEnvironmentProtectionModalOpened,
  ] = useState<boolean>(false)
  const showEnvironmentProtectionModal: boolean = useMemo(() => {
    return environmentProtectionModalOpened && USE_ENV_PROTECTION_MODAL
  }, [USE_ENV_PROTECTION_MODAL, environmentProtectionModalOpened])
  const [
    hasResolvedEnvironmentProtectionModal,
    setHasResolvedEnvironmentProtectionModal,
  ] = useState<boolean>(false)
  const showMaskModal: boolean = useMemo(() => {
    return (
      !IS_PRODUCTION &&
      pageComponentDisplayName != FOUR_O_FOUR_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != FIVE_HUNDRED_PAGE_DISPLAY_NAME &&
      pageComponentDisplayName != ERROR_PAGE_DISPLAY_NAME &&
      ((IS_QA && !hasResolvedEnvironmentProtectionModal) ||
        !USE_ENV_PROTECTION_MODAL)
    )
  }, [
    IS_QA,
    pageComponentDisplayName,
    USE_ENV_PROTECTION_MODAL,
    environmentProtectionModalOpened,
    hasResolvedEnvironmentProtectionModal,
  ])
  if (typeof navigator !== 'undefined')
    useEffect(() => {
      if (isSafari && isMacOs) {
        try {
          const userAgent = navigator.userAgent
          const match = userAgent.match(/Version\/(\d+\.\d+)/)
          if (match && match[1]) {
            setIsMacOsSafariLimitedRender(parseFloat(match[1]) < 16.5)
            setIsMacOsSafariLessThanVersion15(parseFloat(match[1]) < 15)
          }
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
        }
      }
    }, [isMacOs, isSafari])
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    let timeoutId2: NodeJS.Timeout | null = null
    try {
      timeoutId = setTimeout(() => {
        try {
          setEnvironmentProtectionModalOpened(
            USE_ENV_PROTECTION_MODAL &&
              cookies &&
              IS_QA &&
              (!cookies?.bp3_QvdP ||
                cookies?.bp3_QvdP?.toString().trim() != 'true')
          )
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
        } finally {
          timeoutId2 = setTimeout(() => {
            try {
              setHasResolvedEnvironmentProtectionModal(true)
            } catch (ez: any) {
              if (DEBUG_MODE) console.log(ez)
            }
          }, 25)
        }
      }, 10)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
        if (timeoutId2 != null) clearTimeout(timeoutId2)
      } catch {}
    }
  }, [cookies?.bp3_QvdP])
  const [cookieThoughInitiated, setCookieThoughInitiated] =
    useState<boolean>(false)
  useEffect(() => {
    try {
      if (!cookieThoughInitiated) {
        setCookieThoughInitiated(true)
        try {
          init({
            policies: [
              {
                id: 'essential',
                label: translate(
                  'Essential Cookies',
                  'Cookies Esenciales',
                  'Cookies Essentiels',
                  '필수 Cookies',
                  selectedLanguage
                ),
                description: translate(
                  'These essential cookies are necessary for the website to function properly.',
                  'Estas cookies esenciales son necesarias para que el sitio web funcione correctamente.',
                  'Ces cookies essentiels sont nécessaires au bon fonctionnement du site Internet.',
                  '이러한 필수 cookies 는 웹사이트가 제대로 작동하는 데 필요합니다.',
                  selectedLanguage
                ),
                category: 'essential',
              },
              {
                id: 'functional',
                label: translate(
                  'Functional Cookies',
                  'Cookies Funcionales',
                  'Cookies Fonctionnels',
                  '기능적 Cookies',
                  selectedLanguage
                ),
                category: 'functional',
                description: translate(
                  'These cookies are necessary for basic preferences, e.g. language.',
                  'Estas cookies son necesarias para preferencias básicas, p. idioma.',
                  'Ces cookies sont nécessaires pour les préférences de base, par ex. langue.',
                  '이러한 cookies 는 언어 선택과 같은 기본 설정에 필요합니다.',
                  selectedLanguage
                ),
              },
              {
                id: 'statistics',
                label: translate(
                  'Statistics',
                  'Estadísticas',
                  'Statistiques',
                  '통계',
                  selectedLanguage
                ),
                category: 'statistics',
                description: translate(
                  'These are statistical cookies are necessary for the website to function properly.',
                  'Las cookies estadísticas son necesarias para que el sitio web funcione correctamente.',
                  "Il s'agit de cookies statistiques nécessaires au bon fonctionnement du site Internet.",
                  '이는 웹사이트가 제대로 작동하는 데 필요한 통계적 cookies 입니다.',
                  selectedLanguage
                ),
              },
            ],
            essentialLabel: translate(
              'Always on',
              'Siempre Encendido',
              'Toujours Allumé',
              '항상 켜짐',
              selectedLanguage
            ),
            permissionLabels: {
              accept: translate(
                'Accept',
                'Aceptar',
                'Accepter',
                '수락하세',
                selectedLanguage
              ),
              acceptAll: translate(
                'Accept All',
                'Aceptar Todo',
                'Accepter Tout',
                '모든것을 받아 들인다',
                selectedLanguage
              ),
              decline: translate(
                'Decline',
                'Rechazar',
                'Déclin',
                '거절하세요',
                selectedLanguage
              ),
            },
            cookiePreferenceKey: 'cookie-preferences',
            header: {
              title: 'Cookies',
              subTitle: '',
              description: translate(
                'Cookies are used to provide a better experience.',
                'Las cookies se utilizan para proporcionar una mejor experiencia.',
                'Les cookies sont utilisés pour offrir une meilleure expérience.',
                'Cookies 는 더 나은 경험을 제공하는 데 사용됩니다.',
                selectedLanguage
              ),
            },
            customizeLabel: translate(
              'Customize',
              'Personalizar',
              'Personnaliser',
              '사용자 지정',
              selectedLanguage
            ),
          })
        } catch (e) {
          if (DEBUG_MODE) console.log(e)
        }
      } else {
        configure({
          policies: [
            {
              id: 'essential',
              label: translate(
                'Essential Cookies',
                'Cookies Esenciales',
                'Cookies Essentiels',
                '필수 Cookies',
                selectedLanguage
              ),
              description: translate(
                'These essential cookies are necessary for the website to function properly.',
                'Estas cookies esenciales son necesarias para que el sitio web funcione correctamente.',
                'Ces cookies essentiels sont nécessaires au bon fonctionnement du site Internet.',
                '이러한 필수 cookies 는 웹사이트가 제대로 작동하는 데 필요합니다.',
                selectedLanguage
              ),
              category: 'essential',
            },
            {
              id: 'functional',
              label: translate(
                'Functional Cookies',
                'Cookies Funcionales',
                'Cookies Fonctionnels',
                '기능적 Cookies',
                selectedLanguage
              ),
              category: 'functional',
              description: translate(
                'These cookies are necessary for basic preferences, e.g. language.',
                'Estas cookies son necesarias para preferencias básicas, p. idioma.',
                'Ces cookies sont nécessaires pour les préférences de base, par ex. langue.',
                '이러한 cookies 는 언어 선택과 같은 기본 설정에 필요합니다.',
                selectedLanguage
              ),
            },
            {
              id: 'statistics',
              label: translate(
                'Statistics',
                'Estadísticas',
                'Statistiques',
                '통계',
                selectedLanguage
              ),
              category: 'statistics',
              description: translate(
                'These are statistical cookies are necessary for the website to function properly.',
                'Las cookies estadísticas son necesarias para que el sitio web funcione correctamente.',
                "Il s'agit de cookies statistiques nécessaires au bon fonctionnement du site Internet.",
                '이는 웹사이트가 제대로 작동하는 데 필요한 통계적 cookies 입니다.',
                selectedLanguage
              ),
            },
          ],
          essentialLabel: translate(
            'Always on',
            'Siempre Encendido',
            'Toujours Allumé',
            '항상 켜짐',
            selectedLanguage
          ),
          permissionLabels: {
            accept: translate(
              'Accept',
              'Aceptar',
              'Accepter',
              '수락하세',
              selectedLanguage
            ),
            acceptAll: translate(
              'Accept All',
              'Aceptar Todo',
              'Accepter Tout',
              '모든것을 받아 들인다',
              selectedLanguage
            ),
            decline: translate(
              'Decline',
              'Rechazar',
              'Déclin',
              '거절하세요',
              selectedLanguage
            ),
          },
          cookiePreferenceKey: 'cookie-preferences',
          header: {
            title: 'Cookies',
            subTitle: '',
            description: translate(
              'Cookies are used to provide a better experience.',
              'Las cookies se utilizan para proporcionar una mejor experiencia.',
              'Les cookies sont utilisés pour offrir une meilleure expérience.',
              'Cookies 는 더 나은 경험을 제공하는 데 사용됩니다.',
              selectedLanguage
            ),
          },
          customizeLabel: translate(
            'Customize',
            'Personalizar',
            'Personnaliser',
            '사용자 지정',
            selectedLanguage
          ),
        })
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [selectedLanguage])
  const isRunningPwa: boolean = useMediaQuery(
    '(display-mode: standalone)',
    false
  )
  const links = [
    {
      label: translate(
        'Privacy',
        'Privacidad',
        'Confidentialité',
        '개인 정보 보호',
        selectedLanguage
      ),
    },
    {
      label: translate(
        'Terms of Use',
        'Condiciones de Uso',
        "Conditions D'utilisation",
        '이용약관',
        selectedLanguage
      ),
    },
    {
      label: translate(
        'Cookies',
        'Cookies',
        'Cookies',
        'Cookies',
        selectedLanguage
      ),
    },
  ]
  const { isFullscreen } = useFullscreen()
  const [navigating, setNavigating] = useState<boolean>(false)
  const [isBrave, setIsBrave] = useState<boolean>(false)
  const [gotSelectedLanguageFromCookies, setGotSelectedLanguageFromCookies] =
    useState<boolean>(false)
  const [height, setHeight] = useState<any>(null)
  const [width, setWidth] = useState<any>(null)
  const currentYear: string = new Date().getFullYear().toString().trim()
  const [avifCompatible, setAvifCompatible] = useState<boolean>(true)
  const buttonBoxShadow: string = HOME_SPLASH_GET_STARTED_BUTTON_SHADOW
  const buttonRadius: string = HOME_SPLASH_GET_STARTED_BUTTON_BORDER_RADIUS
  useEffect(() => {
    try {
      new Promise(() => {
        try {
          const image: HTMLImageElement = new HTMLImageElement()
          image.onerror = () => setAvifCompatible(false)
          image.src =
            'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A='
        } catch {
          setAvifCompatible(false)
        }
      }).catch(() => setAvifCompatible(false))
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      setAvifCompatible(false)
    }
  }, [])

  useEffect(() => {
    try {
      let tid: NodeJS.Timeout | null = setTimeout(() => {
        let cookieTho: any = document.getElementsByClassName('cookie-though')
        if (cookieTho?.length > 0) {
          cookieTho[0].ariaHidden = true
          cookieTho[0].tabIndex = -1
        }
        try {
          if (tid != null) {
            clearTimeout(tid)
            tid = null
          }
        } catch {}
      }, 1000)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])

  const streamNow: any = useMemo(() => {
    return translate(
      <>Stream Now</>,
      <>Comenzar</>,
      <>Commencer</>,
      <>
        <span aria-hidden={true} className={classes.asianTextBlock}>
          앱
        </span>
      </>,
      selectedLanguage
    )
  }, [selectedLanguage])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    try {
      //@ts-ignore
      if (navigator?.brave)
        timeoutId = setTimeout(async () => {
          let isBrav: boolean = false
          try {
            //@ts-ignore
            isBrav = await navigator?.brave?.isBrave()
          } catch (z: any) {
            if (DEBUG_MODE) console.log(z)
          }
          setIsBrave(isBrav)
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
    return () => {
      try {
        if (timeoutId != null) clearTimeout(timeoutId)
      } catch {}
    }
  })

  useEffect(() => {
    try {
      if (navigator && 'serviceWorker' in navigator) {
        if (!navigator?.serviceWorker?.controller)
          navigator.serviceWorker.register('/practical-stream-blog-sw.js', {
            scope: './',
          })
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])

  function toggleEnvironmentProtectionModal() {
    try {
      try {
        setCookie(ENV_PROTECTION_COOKIE_NAME, 'true', COOKIE_CONFIG)
      } catch (a: any) {
        if (DEBUG_MODE) console.log(a)
      }
      setEnvironmentProtectionModalOpened(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }

  const toggleSelectedLanguage = useCallback(async (selLang: any) => {
    try {
      const lang: string =
        isNotNullOrEmptyString(selLang) &&
        (isEnglish(selLang) ||
          isKorean(selLang) ||
          isFrench(selLang) ||
          isSpanish(selLang))
          ? selLang
          : LANGUAGE_CODE_ENGLISH
      setSelectedLanguage(lang)
      setCookie(SELECTED_LANGUAGE_COOKIE_NAME, lang, COOKIE_CONFIG)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])

  const hasAddedRouterEventHandlers = useRef<boolean>(false)

  if (typeof window !== 'undefined')
    useEffect(() => {
      if (!hasAddedRouterEventHandlers?.current) {
        router.events.on('routeChangeComplete', () => {
          if (!isMobileOnly) {
            let tid: NodeJS.Timeout | null = setTimeout(() => {
              setNavigating(false)
              try {
                if (tid != null) {
                  clearTimeout(tid)
                  tid = null
                }
              } catch {}
            }, 250)
          } else setNavigating(false)
        })
        hasAddedRouterEventHandlers.current = true
      }
    }, [])

  const scrollTop = useCallback(async (behavior: any) => {
    try {
      document?.body?.scrollTo({
        top: 0,
        left: 0,
        behavior: behavior,
      })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])

  const navigateRoute = useCallback(async (routerPath: string) => {
    try {
      try {
        scrollTop('instant')
      } catch {}
      setNavigating(true)
      router.push(routerPath)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])

  useEffect(() => {
    try {
      if (!gotSelectedLanguageFromCookies) {
        setGotSelectedLanguageFromCookies(true)
        let selLang: string = LANGUAGE_CODE_ENGLISH
        const initLang: string = toTrimmedString(pageProps?.initialLang)
        if (
          isFrench(initLang) ||
          isSpanish(initLang) ||
          isKorean(initLang) ||
          isEnglish(initLang)
        )
          selLang = initLang
        else {
          const navLang: string = getNavigatorLanguage()
          if (isNotNullOrEmptyString(navLang) && isValidLanguage(navLang))
            selLang = navLang
        }
        if (selLang?.length > 2 && selLang[2] == '-')
          selLang = selLang?.substring(0, 2)
        toggleSelectedLanguage(selLang)
      }
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])

  useEffect(() => {
    try {
      const nextDiv = document.getElementById('__next')
      if (nextDiv) {
        nextDiv.style.backgroundColor = '#000000'
      }
    } catch {}
  }, [avifCompatible])

  useEffect(() => {
    try {
      if (rateLimited) window.location.href = HOST + '/rate-limited.html'
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [rateLimited])

  const broadcastDimensions = useCallback((h: any, w: any) => {
    try {
      setHeight(h)
      setWidth(w)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [])

  const [canRenderFooter, setCanRenderFooter] = useState<boolean>(false)

  useEffect(() => {
    try {
      setCanRenderFooter(false)
      setTimeout(() => {
        setCanRenderFooter(true)
      }, 1000)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
    }
  }, [pageComponentDisplayName])

  const items: any = useMemo(() => {
    return links.map((link) => (
      <React.Fragment key={link?.label}>
        {isNotNullOrEmptyString(link?.label) && (
          <>
            {
              <div
                className={classes.footerItem}
                onClick={(event) => {
                  try {
                    event.preventDefault()
                    // @ts-ignore
                    let target: any = event?.target?.innerHTML
                      ?.toString()
                      .trim()
                      .toUpperCase()
                      .replaceAll('&NBSP;', '')
                    switch (target) {
                      case translate(
                        'PRIVACY',
                        'PRIVACIDAD',
                        'CONFIDENTIALITÉ',
                        '개인 정보 보호',
                        selectedLanguage
                      ):
                        window.open(
                          translate(
                            MAIN_SITE_ENVIRONMENT_HOST,
                            MAIN_SITE_ENVIRONMENT_HOST_ES,
                            MAIN_SITE_ENVIRONMENT_HOST_FR,
                            MAIN_SITE_ENVIRONMENT_HOST_KO,
                            selectedLanguage
                          ) + '?ds=1'
                        )
                        break
                      case translate(
                        'TERMS OF USE',
                        'CONDICIONES DE USO',
                        "CONDITIONS D'UTILISATION",
                        '이용약관',
                        selectedLanguage
                      ):
                        window.open(
                          translate(
                            MAIN_SITE_ENVIRONMENT_HOST,
                            MAIN_SITE_ENVIRONMENT_HOST_ES,
                            MAIN_SITE_ENVIRONMENT_HOST_FR,
                            MAIN_SITE_ENVIRONMENT_HOST_KO,
                            selectedLanguage
                          ) + '?ds=2'
                        )
                        break
                      case translate(
                        'COOKIES',
                        'COOKIES',
                        'COOKIES',
                        'COOKIES',
                        selectedLanguage
                      ):
                        let cookieThough =
                          document.getElementsByClassName('cookie-though')
                        if (cookieThough?.length > 0) {
                          //@ts-ignore
                          cookieThough[0].style.visibility = 'visible'
                        }
                        show()
                        break
                    }
                  } catch (e) {
                    if (DEBUG_MODE) console.log(e)
                  }
                }}
                style={{
                  display:
                    link?.label == 'Cookies' && isBrave ? 'none' : 'block',
                  fontSize:
                    isSpanish(selectedLanguage) && width < 300
                      ? '11px'
                      : isFrench(selectedLanguage) && width < 315
                      ? '10px'
                      : width < 600
                      ? '12px'
                      : '14px',
                }}
              >
                {link.label}
              </div>
            }
          </>
        )}
      </React.Fragment>
    ))
  }, [links, width, isBrave, selectedLanguage])

  const hasArticle: boolean =
    toTrimmedString(pageProps?.jsonLdType) == 'article'

  const headTag: JSX.Element = useMemo(() => {
    return (
      <Head>
        {IS_PRODUCTION && USE_GOOGLE_ANALYTICS && (
          <>
            <script
              async
              src={
                'https://www.googletagmanager.com/gtag/js?id=' +
                GOOGLE_ANALYTICS_TAG
              }
            ></script>
            {googleAnalyticsTag}
          </>
        )}
        <title>{toTrimmedString(pageProps?.titleTag)}</title>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html:
              `{
                "@context": "https://schema.org",
                "@graph": [{
                              "@type" : "WebSite",
                              "@id" : "${PRODUCTION_HOST}#website",
                              "name" : "${pageProps?.jsonLdSiteName}",
                              "url" : "${PRODUCTION_HOST}"
                        },
                            ` +
              (hasArticle
                ? `  {
                                  "@type": "Blog",
                                  "@id": "${PRODUCTION_HOST}#blog",
                                  "author": {
                                    "@type": ["Organization"],
                                    "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization",
                                    "name": "${STREAMY3_INFORMAL_BUSINESS_NAME}",
                                    "address": {
                                      "@type": "PostalAddress",
                                      "streetAddress": "${STREAMY3_OFFICIAL_ADDRESS_LINE_1}",
                                      "addressLocality": "${STREAMY3_OFFICIAL_ADDRESS_CITY}",
                                      "addressRegion": "${STREAMY3_OFFICIAL_ADDRESS_STATE_CODE}",
                                      "postalCode": "${STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE}",
                                      "addressCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}"
                                    },
                                    "url": "${MAIN_SITE_PRODUCTION_HOST}"
                                  },
                                  "publisher": {
                                    "@type": ["Organization"],
                                    "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization",
                                    "name": "${STREAMY3_INFORMAL_BUSINESS_NAME}",
                                    "address": {
                                      "@type": "PostalAddress",
                                      "streetAddress": "${STREAMY3_OFFICIAL_ADDRESS_LINE_1}",
                                      "addressLocality": "${STREAMY3_OFFICIAL_ADDRESS_CITY}",
                                      "addressRegion": "${STREAMY3_OFFICIAL_ADDRESS_STATE_CODE}",
                                      "postalCode": "${STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE}",
                                      "addressCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}"
                                    },
                                    "url": "${MAIN_SITE_PRODUCTION_HOST}",
                                    "logo": {
                                      "@type": "ImageObject",
                                      "url": "${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES}",
                                      "width": ${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES_PIXELS_WIDTH},
                                      "height": ${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES_PIXELS_HEIGHT}
                                    },
                                    "image": "${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES}" 
                                  },
                                  "isPartOf": {
                                    "@id": "${PRODUCTION_HOST}#website"
                                  }
                                },
                                {
                                "@type": "BlogPosting",
                                "@id": "${pageProps?.jsonLdArticleUrl}#blogposting",
                                "headline": "${pageProps?.jsonLdArticleHeadline}",
                                "image": {
                                  "@type": "ImageObject",
                                  "url": "${pageProps?.jsonLdArticleImage1200x1200}",
                                  "width": 1200,
                                  "height": 1200
                                },
                                "datePublished": "${pageProps?.jsonLdArticleDatePublished}",
                                "dateCreated": "${pageProps?.jsonLdArticleDateCreated}",
                                "url": "${pageProps?.jsonLdArticleUrl}",
                                "inLanguage": "${pageProps?.jsonLdArticleInLanguage}",
                                "keywords": "${pageProps?.jsonLdArticleKeywords}",
                                "author": {
                                    "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization"
                                },
                                "publisher": {
                                    "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization"
                                },
                                "isPartOf": {
                                  "@id": "${PRODUCTION_HOST}#blog"
                                }
                              }
                            ]}
                  `
                : `  {
                              "@type": "Blog",
                              "@id": "${PRODUCTION_HOST}#blog",
                              "url": "${PRODUCTION_HOST}",
                              "name": "${toTrimmedString(
                                pageProps?.jsonLdBlogTitle
                              )}",
                              "description": "${translate(
                                BLOG_DESCRIPTION_HOME,
                                BLOG_DESCRIPTION_HOME_ES,
                                BLOG_DESCRIPTION_HOME_FR,
                                BLOG_DESCRIPTION_HOME_KO,
                                selectedLanguage
                              )}",
                              "author": {
                                "@type": ["Organization"],
                                "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization",
                                "name": "${STREAMY3_INFORMAL_BUSINESS_NAME}",
                                "address": {
                                  "@type": "PostalAddress",
                                  "streetAddress": "${STREAMY3_OFFICIAL_ADDRESS_LINE_1}",
                                  "addressLocality": "${STREAMY3_OFFICIAL_ADDRESS_CITY}",
                                  "addressRegion": "${STREAMY3_OFFICIAL_ADDRESS_STATE_CODE}",
                                  "postalCode": "${STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE}",
                                  "addressCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}"
                                },
                                "url": "${MAIN_SITE_PRODUCTION_HOST}"
                              },
                              "publisher": {
                                "@type": ["Organization"],
                                "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization",
                                "name": "${STREAMY3_INFORMAL_BUSINESS_NAME}",
                                "address": {
                                  "@type": "PostalAddress",
                                  "streetAddress": "${STREAMY3_OFFICIAL_ADDRESS_LINE_1}",
                                  "addressLocality": "${STREAMY3_OFFICIAL_ADDRESS_CITY}",
                                  "addressRegion": "${STREAMY3_OFFICIAL_ADDRESS_STATE_CODE}",
                                  "postalCode": "${STREAMY3_OFFICIAL_ADDRESS_ZIP_CODE}",
                                  "addressCountry": "${UNITED_STATES_ISO_3166_ALPHA_2_CODE}"
                                },
                                "url": "${MAIN_SITE_PRODUCTION_HOST}",
                                "logo": {
                                  "@type": "ImageObject",
                                  "url": "${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES}",
                                  "width": ${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES_PIXELS_WIDTH},
                                  "height": ${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES_PIXELS_HEIGHT}
                                },
                                "image": "${JSON_LD_AUTHOR_AND_PUBLISHER_IMAGE_FOR_GOOGLE_TOP_STORIES}" 
                              },
                              "isPartOf": {
                                "@id": "${PRODUCTION_HOST}#website"
                              },
                              "inLanguage": "${
                                pageProps?.jsonLdArticleInLanguage
                              }",
                              "blogPost": [
                                {
                                  "@type": "BlogPosting",
                                  "@id": "${translate(
                                    HOW_MUCH_ARTICLE_URL,
                                    HOW_MUCH_ARTICLE_URL_ES,
                                    HOW_MUCH_ARTICLE_URL_FR,
                                    HOW_MUCH_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}#blogposting",
                                  "inLanguage": "${
                                    pageProps?.jsonLdArticleInLanguage
                                  }",
                                  "author": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "publisher": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "headline": "${translate(
                                    HOW_MUCH_ARTICLE_TITLE,
                                    HOW_MUCH_ARTICLE_TITLE_ES,
                                    HOW_MUCH_ARTICLE_TITLE_FR,
                                    HOW_MUCH_ARTICLE_TITLE_KO,
                                    selectedLanguage
                                  )}",
                                  "image": {
                                    "@type": "ImageObject",
                                    "url": "${HOW_MUCH_JSON_LD_IMAGE_1200_X_1200}",
                                    "width": 1200,
                                    "height": 1200
                                  },
                                  "url": "${translate(
                                    HOW_MUCH_ARTICLE_URL,
                                    HOW_MUCH_ARTICLE_TITLE_ES,
                                    HOW_MUCH_ARTICLE_TITLE_FR,
                                    HOW_MUCH_ARTICLE_TITLE_KO,
                                    selectedLanguage
                                  )}"
                                },
                                {
                                  "@type": "BlogPosting",
                                  "@id": "${translate(
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL,
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL_ES,
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL_FR,
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}#blogposting",
                                  "inLanguage": "${
                                    pageProps?.jsonLdArticleInLanguage
                                  }",
                                  "author": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "publisher": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "headline": "${translate(
                                    HOW_MUSIC_BOOSTS_ARTICLE_TITLE,
                                    HOW_MUSIC_BOOSTS_ARTICLE_TITLE_ES,
                                    HOW_MUSIC_BOOSTS_ARTICLE_TITLE_FR,
                                    HOW_MUSIC_BOOSTS_ARTICLE_TITLE_KO,
                                    selectedLanguage
                                  )}",
                                  "image": {
                                    "@type": "ImageObject",
                                    "url": "${HOW_MUSIC_BOOSTS_JSON_LD_IMAGE_1200_X_1200}",
                                    "width": 1200,
                                    "height": 1200
                                  },
                                  "url": "${translate(
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL,
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL_ES,
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL_FR,
                                    HOW_MUSIC_BOOSTS_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}"
                                },
                                {
                                  "@type": "BlogPosting",
                                  "@id": "${translate(
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_ES,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_FR,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}#blogposting",
                                  "inLanguage": "${
                                    pageProps?.jsonLdArticleInLanguage
                                  }",
                                  "author": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "publisher": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "headline": "${translate(
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE_ES,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE_FR,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_TITLE_KO,
                                    selectedLanguage
                                  )}",
                                  "image": {
                                    "@type": "ImageObject",
                                    "url": "${HOW_THE_RIGHT_JSON_LD_IMAGE_1200_X_1200}",
                                    "width": 1200,
                                    "height": 1200
                                  },
                                  "url": "${translate(
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_ES,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_FR,
                                    HOW_THE_RIGHT_PLAYLIST_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}"
                                },
                                {
                                  "@type": "BlogPosting",
                                  "@id": "${translate(
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_ES,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_FR,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}#blogposting",
                                  "inLanguage": "${
                                    pageProps?.jsonLdArticleInLanguage
                                  }",
                                  "author": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "publisher": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "headline": "${translate(
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE_ES,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE_FR,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_TITLE_KO,
                                    selectedLanguage
                                  )}",
                                  "image": {
                                    "@type": "ImageObject",
                                    "url": "${SECRET_SOUNDTRACK_FORMULA_JSON_LD_IMAGE_1200_X_1200}",
                                    "width": 1200,
                                    "height": 1200
                                  },
                                  "url": "${translate(
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_ES,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_FR,
                                    SECRET_SOUNDTRACK_FORMULA_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}"
                                },
                                {
                                  "@type": "BlogPosting",
                                  "@id": "${translate(
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL_ES,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL_FR,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}#blogposting",
                                  "inLanguage": "${
                                    pageProps?.jsonLdArticleInLanguage
                                  }",
                                  "author": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "publisher": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "headline": "${translate(
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE_ES,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE_FR,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_TITLE_KO,
                                    selectedLanguage
                                  )}",
                                  "image": {
                                    "@type": "ImageObject",
                                    "url": "${SPOTIFY_ISNT_LEGAL_JSON_LD_IMAGE_1200_X_1200}",
                                    "width": 1200,
                                    "height": 1200
                                  },
                                  "url": "${translate(
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL_ES,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL_FR,
                                    SPOTIFY_ISNT_LEGAL_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}"
                                },
                                {
                                  "@type": "BlogPosting",
                                  "@id": "${translate(
                                    HOW_TO_BUILD_ARTICLE_URL,
                                    HOW_TO_BUILD_ARTICLE_URL_ES,
                                    HOW_TO_BUILD_ARTICLE_URL_FR,
                                    HOW_TO_BUILD_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}#blogposting",
                                  "inLanguage": "${
                                    pageProps?.jsonLdArticleInLanguage
                                  }",
                                  "author": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "publisher": { "@id": "${MAIN_SITE_PRODUCTION_HOST}#organization" },
                                  "headline": "${translate(
                                    HOW_TO_BUILD_ARTICLE_TITLE,
                                    HOW_TO_BUILD_ARTICLE_TITLE_ES,
                                    HOW_TO_BUILD_ARTICLE_TITLE_FR,
                                    HOW_TO_BUILD_ARTICLE_TITLE_KO,
                                    selectedLanguage
                                  )}",
                                  "image": {
                                    "@type": "ImageObject",
                                    "url": "${HOW_TO_BUILD_JSON_LD_IMAGE_1200_X_1200}",
                                    "width": 1200,
                                    "height": 1200
                                  },
                                  "url": "${translate(
                                    HOW_TO_BUILD_ARTICLE_URL,
                                    HOW_TO_BUILD_ARTICLE_URL_ES,
                                    HOW_TO_BUILD_ARTICLE_URL_FR,
                                    HOW_TO_BUILD_ARTICLE_URL_KO,
                                    selectedLanguage
                                  )}"
                                }
                              ]
                                }]}`),
          }}
        />
        {(!IS_PRODUCTION ||
          pageComponentDisplayName == FOUR_O_FOUR_PAGE_DISPLAY_NAME ||
          pageComponentDisplayName == FIVE_HUNDRED_PAGE_DISPLAY_NAME ||
          pageComponentDisplayName == ERROR_PAGE_DISPLAY_NAME) && (
          <meta name="robots" content="noindex" />
        )}
        {isMobileSafari ? (
          <meta
            name="viewport"
            content="initial-scale = 1.0,maximum-scale = 1.0"
          />
        ) : isMobileOnly && !isRunningPwa ? (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes"
          />
        ) : (
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width"
          />
        )}
        <meta
          name="description"
          content={toTrimmedString(pageProps?.metaDescription)}
        />
        <meta
          name="keywords"
          content={toTrimmedString(pageProps?.metaKeywords)}
        />
        <meta name="copyright" content={STREAMY3_OFFICIAL_BUSINESS_NAME} />
        <meta
          property="og:url"
          content={toTrimmedString(pageProps?.ogPageUrl)}
        />
        <meta
          property="og:site_url"
          content={toTrimmedString(pageProps?.ogSiteUrl)}
        />
        <meta
          property="og:site_name"
          content={toTrimmedString(pageProps?.ogSiteName)}
        />
        <meta
          property="og:type"
          content={toTrimmedString(pageProps?.jsonLdType)}
        />
        <meta
          property="og:title"
          content={toTrimmedString(pageProps?.jsonLdTitle)}
        />
        <meta
          property="og:description"
          content={toTrimmedString(pageProps?.jsonLdDescription)}
        />
        <meta
          property="og:image"
          content={toTrimmedString(pageProps?.ogPageImage)}
        />
        <meta
          property="og:image:secure_url"
          content={toTrimmedString(pageProps?.ogPageImage)}
        />
        <meta property="og:image:width" content={OG_IMAGE_WIDTH} />
        <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />
        <meta
          property="og:image:alt"
          content={toTrimmedString(pageProps?.jsonLdDescription)}
        />
        <meta name="mobile-web-app-capable" content="yes" />
        {isApple && (
          <>
            <meta
              name="apple-mobile-web-app-title"
              content="Practical Stream Blog"
            />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta
              name="apple-mobile-web-app-status-bar-style"
              content="black"
            />
          </>
        )}
        {APPLE_STORE_LISTED && (
          <meta
            name="apple-itunes-app"
            content="app-id=APP_ID,affiliate-data=AFFILIATE_ID,app-argument=SOME_TEXT"
          />
        )}
        <meta name="pinterest-rich-pin" content="true" />
        <meta name="application-name" content="Practical Stream Blog" />
        <link
          rel="alternate"
          type="text/plain"
          title="LLM-friendly Summary"
          href="/llms.txt"
        />
        {pageComponentDisplayName == HOME_PAGE_DISPLAY_NAME && (
          <link
            rel="alternate"
            type="text/plain"
            title="Full LLM Context"
            href="/llms-full.txt"
          />
        )}
        <>
          <link
            rel="icon"
            type="image/png"
            sizes="512x512"
            href={ANDROID_CHROME_512_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="384x384"
            href={ANDROID_CHROME_384_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="256x256"
            href={ANDROID_CHROME_256_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="192x192"
            href={ANDROID_CHROME_192_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="144x144"
            href={ANDROID_CHROME_144_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="128x128"
            href={ANDROID_CHROME_128_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="72x72"
            href={ANDROID_CHROME_72_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="48x48"
            href={ANDROID_CHROME_48_ICON}
          />
          <link
            rel="icon"
            type="image/png"
            sizes="36x36"
            href={ANDROID_CHROME_36_ICON}
          />
          {isApple && (
            <>
              <link
                rel="apple-touch-icon"
                sizes="180x180"
                href={APPLE_TOUCH_180_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="152x152"
                href={APPLE_TOUCH_152_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="144x144"
                href={APPLE_TOUCH_144_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="120x120"
                href={APPLE_TOUCH_120_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="114x114"
                href={APPLE_TOUCH_114_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="76x76"
                href={APPLE_TOUCH_76_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="72x72"
                href={APPLE_TOUCH_72_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="60x60"
                href={APPLE_TOUCH_60_ICON}
              />
              <link
                rel="apple-touch-icon"
                sizes="57x57"
                href={APPLE_TOUCH_57_ICON}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_1}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_2}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_3}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_4}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_5}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_6}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_7}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_8}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_9}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_10}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_11}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_12}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_13}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_14}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_15}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_16}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
                href={APPLE_TOUCH_STARTUP_IMAGE_17}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_18}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_19}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_20}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_21}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_22}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_23}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_24}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_25}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_26}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_27}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_28}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_29}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_30}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_31}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_32}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_33}
              />
              <link
                rel="apple-touch-startup-image"
                media="screen and (device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
                href={APPLE_TOUCH_STARTUP_IMAGE_34}
              />
              <link
                rel="mask-icon"
                href={SAFARI_PINNED_TAB_CDN_OR_LOCAL}
                color="#000000"
              />
            </>
          )}
          <meta name="msapplication-TileColor" content="#000000" />
          <meta
            name="msapplication-TileImage"
            content={MS_TILE_144_CDN_OR_LOCAL}
          />
          <meta name="theme-color" content="#000000" />
          <link rel="icon" href={FAV_ICON_CDN_OR_LOCAL} type="image/x-icon" />
          <link rel="icon" href={SVG_ICON_CDN_OR_LOCAL} type="image/svg+xml" />
          <link
            rel="icon"
            type="image/png"
            sizes="194x194"
            href={FAV_ICON_194}
          />
          <link rel="icon" type="image/png" sizes="96x96" href={FAV_ICON_96} />
          <link rel="icon" type="image/png" sizes="32x32" href={FAV_ICON_32} />
          <link rel="icon" type="image/png" sizes="16x16" href={FAV_ICON_16} />
        </>
        {isNotNullOrEmptyString(pageProps?.canonicalUrl) && (
          <link
            rel="canonical"
            href={toTrimmedString(pageProps?.canonicalUrl)}
          />
        )}
        {translate(
          <link rel="manifest" href="/manifest.json" />,
          <link rel="manifest" href="/manifest-es.json" />,
          <link rel="manifest" href="/manifest-fr.json" />,
          <link rel="manifest" href="/manifest-ko.json" />,
          selectedLanguage
        )}
      </Head>
    )
  }, [isRunningPwa, isMobileSafari, selectedLanguage])

  return (
    <>
      {!inIframe ? (
        <>
          {headTag}
          {!isMacOsSafariLessThanVersion15 ? (
            <CookiesProvider>
              <ColorSchemeProvider
                colorScheme={colorScheme}
                toggleColorScheme={toggleColorScheme}
              >
                <MantineProvider
                  withGlobalStyles
                  withNormalizeCSS
                  theme={{
                    colorScheme: colorScheme,
                    colors: MANTINE_DARK_COLORS_THEME,
                  }}
                >
                  <NotificationsProvider>
                    <>
                      {showEnvironmentProtectionModal && (
                        <EnvironmentProtectionModal
                          opened={showEnvironmentProtectionModal}
                          toggleEnvironmentProtectionModal={
                            toggleEnvironmentProtectionModal
                          }
                          selectedLanguage={selectedLanguage}
                          width={width}
                        />
                      )}
                      {showMaskModal && (
                        <>
                          <MaskModal
                            avifCompatible={avifCompatible}
                            opened={showMaskModal}
                            isMacOsSafariLimitedRender={
                              isMacOsSafariLimitedRender
                            }
                            width={width}
                            height={height}
                          />
                        </>
                      )}
                      <div className={'header-band'}></div>
                      <div className={classes.logoWrapper}>
                        <span
                          className={classes.logoLink}
                          style={{
                            pointerEvents:
                              pageComponentDisplayName == 'HomePage'
                                ? 'none'
                                : 'initial',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            try {
                              navigateRoute('/')
                            } catch (e) {
                              if (DEBUG_MODE) console.log(e)
                            }
                          }}
                        >
                          <Image
                            src={avifCompatible ? LOGO_AVIF : LOGO_PNG}
                            alt=""
                            width={146}
                            height={100}
                            sizes="(max-width: 430px) 120px, 
                                      146px"
                            className={classes.imgLogo}
                            priority={true}
                          />
                        </span>
                      </div>
                      {USE_VERCEL_ANALYTICS && (
                        <Analytics debug={!IS_PRODUCTION} />
                      )}
                      <div className={classes.streamNowButtonWrapper}>
                        <Button
                          size="xl"
                          onClick={() => {
                            try {
                              window.location.href = MAIN_SITE_ENVIRONMENT_HOST
                            } catch (e) {
                              if (DEBUG_MODE) console.log(e)
                            }
                          }}
                          style={{
                            marginRight: '20px',
                            outline: '0',
                            textAlign: 'center',
                            lineHeight: 1.2,
                            boxShadow: buttonBoxShadow,
                            visibility: canRenderComponent
                              ? 'visible'
                              : 'hidden',
                          }}
                          styles={(theme) => ({
                            outline: '0',
                            leftIcon: {
                              outline: '0',
                              '&:focus': {
                                outline: '0',
                              },
                              '&:hover': {
                                outline: '0',
                              },
                            },
                            rightIcon: {
                              outline: '0',
                              '&:focus': {
                                outline: '0',
                              },
                              '&:hover': {
                                outline: '0',
                              },
                            },
                            inner: {
                              outline: '0',
                              '&:focus': {
                                outline: '0',
                              },
                              '&:hover': {
                                outline: '0',
                              },
                            },
                            root: {
                              display: width < 330 ? 'none' : 'initial',
                              borderLeft: 'none',
                              borderRight: 'none',
                              backgroundImage:
                                'linear-gradient(to right, #4f1669ff, #8638a9)',
                              color: '#ffffff',
                              borderRadius: buttonRadius,
                              fontFamily: 'Arial, sans-serif',
                              height: 'auto',
                              width: 'auto',
                              paddingTop: '10px',
                              paddingBottom: '10px',
                              paddingLeft: '20px',
                              paddingRight: '20px',
                              fontSize: '25px',
                              outline: '0',
                              '&:focus': {
                                outline: '0',
                              },
                              '&:hover': {
                                outline: '0',
                                color: '#ffffff',
                                backgroundImage:
                                  'linear-gradient(to right, #3c0a54ff, #8638a9)',
                              },
                              [theme.fn.smallerThan(500)]: {
                                fontSize: '25px',
                                width: isKorean(selectedLanguage)
                                  ? 'initial'
                                  : '200px',
                              },
                              [theme.fn.smallerThan(430)]: {
                                width: isKorean(selectedLanguage)
                                  ? 'initial'
                                  : '150px',
                                fontSize: '18.5px',
                              },
                              [theme.fn.smallerThan(360)]: {
                                width: isKorean(selectedLanguage)
                                  ? 'initial'
                                  : '130px',
                                fontSize: '14.5px',
                              },
                            },
                          })}
                        >
                          {streamNow}
                        </Button>
                      </div>
                      <div
                        style={{
                          visibility: canRenderComponent ? 'visible' : 'hidden',
                        }}
                      >
                        <Component
                          {...pageProps}
                          isBot={isBot}
                          width={width}
                          height={height}
                          navigateRoute={navigateRoute}
                          avifCompatible={avifCompatible}
                          selectedLanguage={selectedLanguage}
                          broadcastDimensions={broadcastDimensions}
                        />
                      </div>
                      {canRenderFooter && !navigating && (
                        <div
                          style={{
                            minHeight:
                              width > 767
                                ? '100px'
                                : width > 500
                                ? '100px'
                                : isIOS
                                ? '200px'
                                : '150px',
                            maxHeight:
                              width > 767
                                ? '160px'
                                : width > 500
                                ? '190px'
                                : isIOS
                                ? '249ox'
                                : '190px',
                            marginTop: 'auto',
                            transform: 'translate(-10px, 0px)',
                            width:
                              width > 485
                                ? '102vw'
                                : width > 322
                                ? '103vw'
                                : '104vw',
                            zIndex: 3290,
                            backgroundColor: '#000000',
                            borderTop: '.5px solid #ffffff',
                          }}
                        >
                          <div
                            className={classes.inner}
                            style={{
                              padding: '8px 8px',
                            }}
                          >
                            <Group
                              className={classes.links}
                              spacing={width > 355 ? 10 : 5}
                              style={{
                                marginTop: '0px',
                                justifyContent: 'space-evenly',
                              }}
                            >
                              {items}
                            </Group>
                          </div>
                          <div
                            className={classes.innerBottom}
                            style={{
                              padding: width < 767 ? '10px 16px' : '32px 16px',
                            }}
                          >
                            <Text className={classes.copyrightTrademark}>
                              &copy;&nbsp;{currentYear}&nbsp;
                              {STREAMY3_OFFICIAL_BUSINESS_NAME}.
                            </Text>
                          </div>
                          {(isMobile || isMobileOnly) &&
                            !isRunningPwa &&
                            !isFullscreen && (
                              <div
                                style={{
                                  backgroundColor: '#000000',
                                }}
                              >
                                <>
                                  <br />
                                  <br />
                                  <br />
                                  <br />
                                </>
                              </div>
                            )}
                        </div>
                      )}
                    </>
                  </NotificationsProvider>
                </MantineProvider>
              </ColorSchemeProvider>
            </CookiesProvider>
          ) : (
            <div
              style={{
                width: '100vw',
                height: '100vh',
                overflow: 'hidden',
                backgroundColor: '#000000',
                position: 'fixed',
                left: 0,
                top: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <img
                alt={translate(
                  'Logo',
                  'Logotipo',
                  'Logo',
                  '로고',
                  selectedLanguage
                )}
                style={{
                  marginBottom: '25px',
                  visibility: 'visible',
                  maxHeight:
                    width < 992
                      ? width > height
                        ? '20vh'
                        : height > 800 && width < 600
                        ? '17vh'
                        : '15vh'
                      : '19vh',
                  height:
                    width < 400
                      ? 'initial'
                      : width < 992
                      ? width > height
                        ? '20vh'
                        : height > 800 && width < 600
                        ? '7vh'
                        : '10vh'
                      : '19vh',
                }}
                draggable="false"
                src={PRACTICAL_STREAM_LOGO_LARGE_DARK_PNG}
                //@ts-ignore
                nopin="nopin"
                referrerPolicy="no-referrer"
              />
              <p
                style={{
                  color: '#339af0',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '20px',
                }}
              >
                {translate(
                  <>No support for Safari v.15 and lower.</>,
                  <>No es compatible con Safari v.15 y anteriores.</>,
                  <>Non compatible avec Safari v.15 et versions antérieures.</>,
                  <>
                    <span className={classes.asianTextBlock} aria-hidden={true}>
                      Safari
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      v.15
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이전
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      버전과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      호환되지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          {headTag}
          <p>&nbsp;</p>
        </>
      )}
    </>
  )
}
